import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

/** Path (relative to `buildUrl`) for a player's co-guardian circle. */
export const playerGuardiansEndpoint = (playerId: string) =>
  `/guardians/me/players/${playerId}/guardians`;

export enum PlayerGuardianStatus {
  Active = 'active',
  Waiting = 'waiting',
  Declined = 'declined',
  Expired = 'expired',
}

/**
 * One entry in a player's circle from
 * GET `/api/v1/guardians/me/players/{playerId}/guardians` — either an active member
 * or an outstanding invitation (mirrors the endpoint's response item).
 */
export type PlayerGuardian = {
  id: string;
  guardianUid: string;
  name: string;
  /** Avatar URL; may be empty for members without a photo. */
  photoUrl?: string;
  invitationCode?: string;
  /** Relationship to the player, e.g. "parent" | "grandparent" | "coach" | "other". */
  relationship?: string;
  /** Circle role, e.g. "owner" (primary guardian) | "member". */
  role?: string;
  /** Membership/invitation status. */
  status: PlayerGuardianStatus;
  /** ISO date the member joined (active members). */
  since?: string;
  /** ISO date the invitation was sent (pending / expired / declined). */
  invitedAt?: string;
  /** True for the signed-in guardian's own row. */
  isSelf?: boolean;
  /** True when this member is the profile owner (the primary guardian). */
  isOwner?: boolean;
  /** Invited person's email — present on invitation rows. */
  email?: string;
  /** ISO date the invitation expires — present on invitation rows. */
  expiresAt?: string;
  /** Invitation URL returned upon creation or preview. */
  inviteUrl?: string;
  /** Custom message sent with the invitation. */
  message?: string;
};

/**
 * Lists a player's circle — active members + outstanding invitations — via
 * GET `/api/v1/guardians/me/players/{playerId}/guardians`. Authenticated with the
 * Firebase bearer token.
 */
export async function fetchPlayerGuardians(
  playerId: string,
  options?: RequestOptions,
): Promise<PlayerGuardian[]> {
  return await usKidsGolfService.get<PlayerGuardian[]>(
    buildUrl(playerGuardiansEndpoint(playerId)),
    undefined,
    options,
  );
}
